import { Component, OnInit, ViewChild, NgZone } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { JobFile } from '@app/_models/job-file';
import { JobHistoryView } from '@app/_models/job-history-view';
import { AlertService } from '@app/_services';
import { FunctionsService } from '@app/_services/functions.service';
import { RestService } from '@app/_services/rest.service';
import { MatTableExporterDirective } from 'mat-table-exporter';
import Swal from 'sweetalert2';
import { take } from 'rxjs/operators';

type AoA = any[][];

@Component({
  selector: 'app-invoices-paid',
  templateUrl: './invoices-paid.component.html',
  styleUrls: ['./invoices-paid.component.scss'],
})
export class InvoicesPaidComponent implements OnInit {
  invoicesArray: AoA = [[], []];
  displayedColumns: string[] = [
    'id',
    'refNo',
    'invoiceNumber',
    'invoiceAmount',
    'category',
    'tradingName',
    'instructionTypeId',
    'date',
    'addedBy',
    'timeStamp',
    'details',
    'viewDocument'
  ];

  dataSource!: MatTableDataSource<JobHistoryView>;
  isLoading = true;
  searchValue = localStorage.getItem('searchTextInvoicesPaid')
    ? localStorage.getItem('searchTextInvoicesPaid')
    : '';
  @ViewChild(MatTableExporterDirective, { static: true }) exporter!: MatTableExporterDirective;
  @ViewChild(MatSort, { static: true })
  matSort: MatSort = new MatSort();
  paginator!: MatPaginator;
  @ViewChild(MatPaginator) set _paginator(paginator: MatPaginator) {
    this.paginator = paginator;
    if (this.dataSource) this.dataSource.paginator = this.paginator;
  }
  pageSizeControl = new FormControl(
    localStorage.getItem('invoicePaidPageSize')
      ? Number(localStorage.getItem('invoicePaidPageSize'))
      : 15
  );
  
  constructor(
    public restService: RestService,
    private alertService: AlertService,
    public functionsService: FunctionsService,
    private ngZone: NgZone
  ) {
    this.getRemoteData();
  }

  ngOnInit(): void {}

  getRemoteData() {
    try {
      this.isLoading = true;
      this.getPaidInvoices();
      return true;
    } catch (error: any) {
      this.isLoading = false;
      this.alertService.error(error);
      return false;
    }
  }

  getPaidInvoices() {
    return this.restService.getPaidInvoices().subscribe(
      (jobHistory: JobHistoryView[]) => {
        this.isLoading = false;
        this.dataSource = new MatTableDataSource(jobHistory);
        this.dataSource.sort = this.matSort;
        this.dataSource.paginator = this.paginator;
         if (
            this.pageSizeControl.value != null &&
            this.pageSizeControl.value > 100 &&
            this.dataSource.data.length !=
              Number(localStorage.getItem('invoicePaidPageSize'))
          ) {
            console.log(
              'pageSizeControl.value: ' + this.dataSource.data.length.toString()
            );
            localStorage.setItem(
              'invoicePaidPageSize',
              this.dataSource.data.length.toString()
            );
          }
      },
      (error) => {
        this.isLoading = false;
        this.alertService.error(error);
      }
    );
  }

  openInvoicePDF(id: number) {
    this.restService.getJobHistoryView(id).subscribe((data) => {
      if (data.jobFileId) {
        this.restService.getInvoicePDF(data.jobFileId).subscribe(
          (data: JobFile) => {
            this.functionsService.openPdf(data.fileData);
          },
          (error) => {
            this.alertService.error(error);
          }
        );
      }
    });
  }

  //Search by keyword
  onSearchKeyUp(event: Event) {
    try {
      var currentFilter = (event.target as HTMLInputElement).value;
      this.dataSource.filter = currentFilter;
      localStorage.setItem('searchTextInvoicesPaid', currentFilter);
      
    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
    } catch (error) {
      Swal.fire('Error in Search by keyword : ' + error, '', 'error');
    }
  }

  //Clear search by keyword
  onClearClicked(search: { value: string }) {
    this.dataSource.filter = '';
    search.value = '';
    this.searchValue = '';
    localStorage.setItem('searchTextInvoicesPaid', '');

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  onPageSizeChange() {
    // Save the new page size to localStorage when the user selects a new page size
    localStorage.setItem('invoicePaidPageSize', this.paginator.pageSize.toString());
  }

 exportCurrentPage() {
  this.isLoading = true;

  const paginator = this.dataSource.paginator!;
  const startIndex = paginator.pageIndex * paginator.pageSize;
  const endIndex = startIndex + paginator.pageSize;

  // Use filtered data if filter applied, else use full data
  const activeData = this.dataSource.filteredData ?? this.dataSource.data;

  const currentPageData = activeData.slice(startIndex, endIndex);
  const originalData = this.dataSource.data;

  // Temporarily replace data with current page only
  this.dataSource.data = currentPageData;

  this.exporter.exportTable('xlsx', {
    fileName:
      'Contractor Invoices Paid ' +
      this.functionsService.getCurrentTimestampFormatted(),
  });

  setTimeout(() => {
    // Restore original dataset and paginator
    this.dataSource.data = originalData;
    this.dataSource.paginator = paginator;
    this.isLoading = false;
  });
}

  exportAll() {
    this.isLoading = true; // show spinner immediately

    Promise.resolve().then(() => {
      const paginator = this.dataSource.paginator;
      const oldPageSize = this.pageSizeControl.value;

      // Detach paginator + expand page size to all rows
      this.dataSource.paginator = null;
      const fullData = this.dataSource.data;
      this.pageSizeControl.setValue(fullData.length, { emitEvent: false });

      // Run exporter
      setTimeout(() => { 
        this.exporter.exportTable('xlsx', {
          fileName:
            'Contractor Invoices Paid ' +
            this.functionsService.getCurrentTimestampFormatted(),
        });

        // Restore paginator and pageSize
        this.ngZone.onStable.pipe(take(1)).subscribe(() => {
          this.dataSource.paginator = paginator;
          this.pageSizeControl.setValue(oldPageSize, { emitEvent: false });
          this.isLoading = false;
        });
      }, 0);
    });
  }
}
